import { components } from '../.generated';
import { BaseAPI } from './BaseApi';
import { Problem } from '../types';

// ---------------------------- Query Type Mappings

type DeviceRegisterQueryType = keyof DeviceRegisterQueryMap;

type DeviceRegisterQueryMap = {
  'Cozify.DeviceRegister/GetEntryDeviceRegisterQuery': components['schemas']['GetEntryDeviceRegisterQuery'];
  'Cozify.DeviceRegister/ListEntriesPagedDeviceRegisterQuery': components['schemas']['ListEntriesPagedDeviceRegisterQuery'];
  'Cozify.DeviceRegister/HubDeviceRegisterQuery': components['schemas']['HubDeviceRegisterQuery'];
  'Cozify.DeviceRegister/GetListEntriesColumnParamsDeviceRegisterQuery': components['schemas']['GetListEntriesColumnParamsDeviceRegisterQuery'];
};

type DeviceRegisterQueryResponseMap = {
  'Cozify.DeviceRegister/GetEntryDeviceRegisterQuery': components['schemas']['GetEntryDeviceRegisterQueryResult'];
  'Cozify.DeviceRegister/ListEntriesPagedDeviceRegisterQuery': components['schemas']['ListEntriesPagedDeviceRegisterQueryResult'];
  'Cozify.DeviceRegister/HubDeviceRegisterQuery': components['schemas']['HubDeviceRegisterQueryResult'];
  'Cozify.DeviceRegister/GetListEntriesColumnParamsDeviceRegisterQuery': components['schemas']['GetListEntriesColumnParamsDeviceRegisterQueryResult'];
};

type DeviceRegisterQuery<T extends DeviceRegisterQueryType> = {
  type: T;
} & DeviceRegisterQueryMap[T];

type DeviceRegisterQueryResponse<T extends DeviceRegisterQueryType> =
  DeviceRegisterQueryResponseMap[T];

// ---------------------------- Command Type Mappings

type DeviceRegisterCommandType = keyof DeviceRegisterCommandMap;

type DeviceRegisterCommandMap = {
  'Cozify.DeviceRegister/HubDeviceRegisterCommand': components['schemas']['HubDeviceRegisterCommand'];
  'Cozify.DeviceRegister/CreateEntryDeviceRegisterCommand': components['schemas']['CreateEntryDeviceRegisterCommand'];
  'Cozify.DeviceRegister/DeleteEntryDeviceRegisterCommand': components['schemas']['DeleteEntryDeviceRegisterCommand'];
  'Cozify.DeviceRegister/UpdateEntryDeviceRegisterCommand': components['schemas']['UpdateEntryDeviceRegisterCommand'];
};

type DeviceRegisterCommandResponseMap = {
  'Cozify.DeviceRegister/HubDeviceRegisterCommand': components['schemas']['HubDeviceRegisterCommandResult'];
  'Cozify.DeviceRegister/CreateEntryDeviceRegisterCommand': components['schemas']['CreateEntryDeviceRegisterCommandResult'];
  'Cozify.DeviceRegister/DeleteEntryDeviceRegisterCommand': components['schemas']['DeleteEntryDeviceRegisterCommandResult'];
  'Cozify.DeviceRegister/UpdateEntryDeviceRegisterCommand': components['schemas']['UpdateEntryDeviceRegisterCommandResult'];
};

type DeviceRegisterCommand<T extends DeviceRegisterCommandType> = {
  type: T;
} & DeviceRegisterCommandMap[T];

type DeviceRegisterCommandResponse<T extends DeviceRegisterCommandType> =
  DeviceRegisterCommandResponseMap[T];

// ---------------------------- Api Definition

export class DeviceRegisterApi extends BaseAPI {
  protected _getQueryURL(deviceRegisterId: string) {
    return `${this.config.baseUrl}/Providers/Cozify.DeviceRegister/${deviceRegisterId}/Query`;
  }

  protected _getCommandURL(deviceRegisterId: string) {
    return `${this.config.baseUrl}/Providers/Cozify.DeviceRegister/${deviceRegisterId}/Command`;
  }

  async query<T extends DeviceRegisterQueryType>({
    deviceRegisterId,
    query,
    signal,
  }: {
    deviceRegisterId: string;
    query: DeviceRegisterQuery<T>;
    signal?: AbortSignal;
  }): Promise<DeviceRegisterQueryResponse<T> | Problem> {
    const accessToken = await this.getAccessToken();
    try {
      const res = await fetch(this._getQueryURL(deviceRegisterId), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
        }),
        signal,
      });

      const data = await res.json();
      if (!res.ok) {
        return this._toProblem(data);
      }
      return data.result;
    } catch (error) {
      return this._toProblem({
        type: 'Problem',
        title: 'Network error',
        detail: '',
      });
    }
  }

  async command<T extends DeviceRegisterCommandType>({
    deviceRegisterId,
    command,
    signal,
  }: {
    deviceRegisterId: string;
    command: DeviceRegisterCommand<T>;
    signal?: AbortSignal;
  }): Promise<DeviceRegisterCommandResponse<T> | Problem> {
    const accessToken = await this.getAccessToken();
    try {
      const res = await fetch(this._getCommandURL(deviceRegisterId), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          command,
        }),
        signal,
      });
      const data = await res.json();
      if (!res.ok) {
        return this._toProblem(data);
      }
      return data.result;
    } catch (error) {
      return this._toProblem({
        type: 'Problem',
        title: 'Network error',
        detail: '',
      });
    }
  }
}
